import { ManuteRequestConfig } from '@/api/types';
import { ChecklistItemRequest, ChecklistRequest, CommentRequest, MediaRequest } from '@/api/requests/types';
import { TaskQueryParams, TaskRequest, TaskStatus } from '@/api/requests/work/types';

/**
 * Request to find all tasks.
 * @param params
 */
export const ALL = (params?: TaskQueryParams): ManuteRequestConfig => ({
    method: 'GET',
    url: '/tasks',
    params: {
        page_size: 50,
        ...params,
    },
});

/**
 * Request to save a new task.
 * @param data
 */
export const STORE = (data: TaskRequest): ManuteRequestConfig => ({
    method: 'POST',
    url: `/tasks`,
    data,
});

/**
 * Request to find a task by id.
 * @param taskId
 */
export const FIND = (taskId: number): ManuteRequestConfig => ({
    method: 'GET',
    url: `/tasks/${taskId}`,
});

/**
 * Request to update an existing task.
 * @param taskId
 * @param data
 */
export const UPDATE = (taskId: number, data: TaskRequest): ManuteRequestConfig => ({
    method: 'PUT',
    url: `/tasks/${taskId}`,
    data,
});

/**
 * Request to delete a task by id.
 * @param taskId
 */
export const DELETE = (taskId: number): ManuteRequestConfig => ({
    method: 'DELETE',
    url: `/tasks/${taskId}`,
});

/**
 * Request to update an existing task.
 * @param taskId
 * @param status
 */
export const CHANGE_STATUS = (taskId: number, status: TaskStatus): ManuteRequestConfig => ({
    method: 'POST',
    url: `/tasks/${taskId}/status`,
    data: { status },
});

/**
 * Request to add a checklist to an existing task.
 * @param taskId
 * @param data
 */
export const ADD_CHECKLIST = (taskId: number, data: ChecklistRequest): ManuteRequestConfig => ({
    method: 'POST',
    url: `/tasks/${taskId}/checklists`,
    data,
});

/**
 * Request to update an existing checklist of a task.
 * @param taskId
 * @param checklistId
 * @param data
 */
export const UPDATE_CHECKLIST = (taskId: number, checklistId: number, data: ChecklistRequest): ManuteRequestConfig => ({
    method: 'PUT',
    url: `/tasks/${taskId}/checklists/${checklistId}`,
    data,
});

/**
 * Request to remove an existing checklist from a task.
 * @param taskId
 * @param checklistId
 */
export const REMOVE_CHECKLIST = (taskId: number, checklistId: number): ManuteRequestConfig => ({
    method: 'DELETE',
    url: `/tasks/${taskId}/checklists/${checklistId}`,
});

/**
 * Request to add a checklist item to an existing task.
 * @param taskId
 * @param checklistId
 * @param data
 */
export const ADD_CHECKLIST_ITEM = (
    taskId: number,
    checklistId: number,
    data: ChecklistItemRequest): ManuteRequestConfig => (
{
    method: 'POST',
    url: `/tasks/${taskId}/checklists/${checklistId}/items`,
    data,
});

/**
 * Request to update a checklist item to an existing task.
 * @param taskId
 * @param checklistId
 * @param itemId
 * @param data
 */
export const UPDATE_CHECKLIST_ITEM = (
    taskId: number,
    checklistId: number,
    itemId: number,
    data: ChecklistItemRequest): ManuteRequestConfig => (
{
    method: 'PUT',
    url: `/tasks/${taskId}/checklists/${checklistId}/items/${itemId}`,
    data,
});

/**
 * Request to remove a checklist item from an existing task.
 * @param taskId
 * @param checklistId
 * @param itemId
 */
export const REMOVE_CHECKLIST_ITEM = (
    taskId: number,
    checklistId: number,
    itemId: number): ManuteRequestConfig => (
{
    method: 'DELETE',
    url: `/tasks/${taskId}/checklists/${checklistId}/items/${itemId}`,
});

/**
 * Request to mark a checklist item as checked into an existing task.
 * @param taskId
 * @param checklistId
 * @param itemId
 */
export const MARK_CHECKLIST_ITEM_AS_CHECKED = (
    taskId: number,
    checklistId: number,
    itemId: number): ManuteRequestConfig => (
{
    method: 'POST',
    url: `/tasks/${taskId}/checklists/${checklistId}/items/${itemId}/check`,
});

/**
 * Request to mark a checklist item as unchecked into an existing task.
 * @param taskId
 * @param checklistId
 * @param itemId
 */
export const MARK_CHECKLIST_ITEM_AS_UNCHECKED = (
    taskId: number,
    checklistId: number,
    itemId: number): ManuteRequestConfig => (
{
    method: 'POST',
    url: `/tasks/${taskId}/checklists/${checklistId}/items/${itemId}/uncheck`,
});

/**
 * Request to add a comment to an existing task.
 * @param taskId
 * @param data
 */
export const ADD_COMMENT = (taskId: number, data: CommentRequest): ManuteRequestConfig => ({
    method: 'POST',
    url: `/tasks/${taskId}/comments`,
    data,
});

/**
 * Request to update an existing comment of a task.
 * @param taskId
 * @param commentId
 * @param data
 */
export const UPDATE_COMMENT = (taskId: number, commentId: number, data: CommentRequest): ManuteRequestConfig => ({
    method: 'PUT',
    url: `/tasks/${taskId}/comments/${commentId}`,
    data,
});

/**
 * Request to remove an existing comment from a task.
 * @param taskId
 * @param commentId
 */
export const REMOVE_COMMENT = (taskId: number, commentId: number): ManuteRequestConfig => ({
    method: 'DELETE',
    url: `/tasks/${taskId}/comments/${commentId}`,
});

/**
 * Request to add a media to an existing task.
 *
 * @param taskId
 * @param data
 * @param onUploadProgress
 */
export const ADD_MEDIA = (
    taskId: number,
    data: FormData,
    onUploadProgress: (progressEvent: ProgressEvent) => void = (progressEvent) => { return; },
): ManuteRequestConfig => ({
    method: 'POST',
    url: `/tasks/${taskId}/media`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data,
    onUploadProgress,
});

/**
 * Request to update a media of an existing task.
 *
 * @param taskId
 * @param mediaId
 * @param data
 * @constructor
 */
export const UPDATE_MEDIA = (taskId: number, mediaId: number, data: MediaRequest): ManuteRequestConfig => ({
    method: 'PUT',
    url: `/tasks/${taskId}/media/${mediaId}`,
    data,
});

/**
 * Request to remove an existing media from a task.
 * @param taskId
 * @param mediaId
 */
export const REMOVE_MEDIA = (taskId: number, mediaId: number): ManuteRequestConfig => ({
    method: 'DELETE',
    url: `/tasks/${taskId}/media/${mediaId}`,
});

/**
 * Request to download a existing media of a task.
 * @param taskId
 * @param mediaId
 */
export const DOWNLOAD_MEDIA = (taskId: number, mediaId: number): ManuteRequestConfig => ({
    method: 'GET',
    url: `/tasks/${taskId}/media/${mediaId}/download`,
    responseType: 'blob',
});
